import {Product, ProductType} from '../models/product/product';
import {Book, BookFilter} from '../models/product/book';
import {Filter, SortOption} from '../models/filters/filters';
import {Language} from '../constants/language';
import {getProductsLocalized} from '../mock-data/product/products-mock-data';
import {PaginatedResult, PAGINATION, PaginationUtil} from "../utils/pagination-util";
import {CacheUtil} from "../utils/cache-util";
import {useMockData} from "../constants/environment";

class ProductService {

  private static readonly PRODUCT_DATA_CACHE_KEY = "productContent";

  public async getProducts(
    language: Language = Language.EN,
    productType: ProductType = ProductType.ALL,
    filter?: Filter,
    sort?: SortOption,
    page: number = PAGINATION.DEFAULT_PAGE,
    limit: number = PAGINATION.DEFAULT_PAGE_SIZE,
    fetchAll: boolean = false
  ): Promise<PaginatedResult<Product>> {

    let products: Product[];
    let paginatedResult: PaginatedResult<Product>;

    const cacheKey = `${ProductService.PRODUCT_DATA_CACHE_KEY}-${language}-${productType}`;
    const cachedData = CacheUtil.get<Product[]>(cacheKey);
    if (cachedData) {
      products = cachedData;
      paginatedResult = PaginationUtil.paginate<Product>(products, page, limit);
    }else {
      if (useMockData) {
          await new Promise(resolve => setTimeout(resolve, 300));
          paginatedResult = getProductsLocalized(language, productType, true);
          products = paginatedResult.data;
          CacheUtil.set(cacheKey, products);
      } else{
          paginatedResult = getProductsLocalized(language, productType, true);
          products = paginatedResult.data;
          CacheUtil.set(cacheKey, products);
      }
    }

    if (filter) products = this.applyFilters(products, filter, productType);
    if (sort) products = this.applySorting(products, sort);

    if(filter || sort) paginatedResult = PaginationUtil.paginate<Product>(products, page, limit); //Update Pagination if filtering or sorting applied

    return paginatedResult;
  }


  async getProductById(id: number, language: Language = Language.AR, productType: ProductType = ProductType.ALL): Promise<Product | undefined> {
    const productResults = await this.getProducts(language, productType);
    const products: Product[] = productResults.data ? productResults.data : [];
    return products.find(p => p.id === id);
  }

  async getFeaturedProducts(language: Language = Language.AR, productType: ProductType = ProductType.ALL, limit: number = PAGINATION.DEFAULT_PAGE_SIZE): Promise<Product[]> {
    const productResults = await this.getProducts(language, productType);
    const products: Product[] = productResults.data ? productResults.data : [];

    return products
      .filter(product => product.featured)
      .slice(0, limit);
  }


  async searchProducts(
    query: string,
    language: Language = Language.AR,
    productType?: ProductType,
    filter?: Filter,
    sort?: SortOption
  ): Promise<Product[]> {
    const productResults = await this.getProducts(language, productType, filter, sort);
    const products: Product[] = productResults.data ? productResults.data : [];

    const searchTerm = query.toLowerCase();

    return products.filter(product => {
      const basicMatch = (
          product.name.toLowerCase().includes(searchTerm) ||
          (product.description && product.description.toLowerCase().includes(searchTerm)) ||
          product.tags.some(tag => tag.toLowerCase().includes(searchTerm))
      );

      // Add product-specific search logic
      if (productType === ProductType.BOOK || !productType || productType === ProductType.ALL) {
        const book = product as Book;
        return basicMatch || (book.author && book.author.toLowerCase().includes(searchTerm));
      }

      return basicMatch;
    });
  }


  private applyFilters(products: Product[], filter: Filter, productType: ProductType = ProductType.ALL): Product[] {
    return products.filter(product => {
      // Base filters
      if (filter.priceRange) {
        if (product.price < filter.priceRange.min || product.price > filter.priceRange.max) {
          return false;
        }
      }

      if (filter.inStock !== undefined) {
        if (product.inStock !== filter.inStock) return false;
      }

      if (filter.featured !== undefined) {
        if (product.featured !== filter.featured) return false;
      }

      if (filter.rating !== undefined) {
        if (product.rating < filter.rating) return false;
      }

      // Product type filter
      if (filter.type && filter.type.length > 0) {
        // TODO: Add product type field to Product interface
        // For now, assume books if productType is BOOKS
        if (productType && !filter.type.includes(productType)) return false;
      }

      // Product-specific filters
      if (productType === ProductType.BOOK || !productType || productType === ProductType.ALL) {
        const bookFilter = filter as BookFilter;
        const book = product as Book;

        if (bookFilter.category && bookFilter.category.length > 0) {
          if (book.bookCategory && !bookFilter.category.includes(book.bookCategory)) return false;
        }

        if (bookFilter.format && bookFilter.format.length > 0) {
          if (book.bookFormat && !bookFilter.format.includes(book.bookFormat)) return false;
        }

        if (bookFilter.author && bookFilter.author.length > 0) {
          if (!bookFilter.author.includes(book.author)) return false;
        }

        if (bookFilter.publisher && bookFilter.publisher.length > 0) {
          if (!bookFilter.publisher.includes(book.publisher)) return false;
        }
      }

      return true;
    });
  }

  private applySorting(products: Product[], sort: SortOption): Product[] {
    return [...products].sort((a, b) => {
      let aValue: any = a[sort.field as keyof Product];
      let bValue: any = b[sort.field as keyof Product];

      // Handle date fields
      if (sort.field === 'publishedDate' || sort.field === 'createdAt') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      // Handle string fields
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sort.direction === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  }


}


export const productService = new ProductService();